import { Controller } from '@hotwired/stimulus';
import Rails from '@rails/ujs';
import { z } from 'zod';
import { isProduction } from '../../utils/dev';
import { AU_STATES, BR_STATES, CA_STATES, COUNTRIES_NOT_REQUIRED_ZIP, JP_STATES, MILITARY_US_STATES, US_STATES, } from './consts';
import { getCountries, getCountryNameByCode, getStateNameByCode, isAUZipCode, isCAZipCode, isCPFNumber, isCURPNumber, isEmpty, isPhoneNumber, isRFCNumber, isStringLatin, isUSZipCode, } from './utils';
export default class default_1 extends Controller {
    static targets = [
        'countrySelect',
        'stateInput',
        'stateSelect',
        'brVatIdInput',
        'mxVatIdInput',
        'submitButton',
        'errorModal',
        'errorModalContent',
        'suggestionModal',
        'suggestionModalContent',
    ];
    static values = {
        country: { type: String, default: '' },
        state: { type: String, default: '' },
    };
    connect() {
        this.generateCountriesOptions();
        if (this.countryValue) {
            this.handleCountryChange();
        }
    }
    generateCountriesOptions() {
        const countries = getCountries();
        Object.keys(countries).forEach((country) => {
            const option = document.createElement('option');
            option.innerHTML = countries[country].name;
            option.value = country;
            if (country === this.countryValue) {
                option.selected = true;
            }
            this.countrySelectTarget.append(option);
        });
        if (!this.countryValue) {
            const option = document.createElement('option');
            option.value = '';
            option.innerHTML = 'Country';
            option.selected = true;
            option.hidden = true;
            this.countrySelectTarget.append(option);
        }
    }
    generateStateSelectOptions(states) {
        const select = this.stateSelectTarget.querySelector('select');
        if (!select)
            return;
        this.hideStateInput();
        this.showStateSelect();
        select.innerHTML = '';
        select.value = '';
        states.forEach(({ name, abbreviation }) => {
            const option = document.createElement('option');
            option.innerHTML = name;
            option.value = abbreviation;
            if (abbreviation === this.stateValue) {
                option.selected = true;
            }
            select.append(option);
        });
        if (!this.stateValue) {
            const option = document.createElement('option');
            option.value = '';
            option.innerHTML = 'State';
            option.selected = true;
            option.hidden = true;
            select.append(option);
        }
    }
    handleCountryChange() {
        const formValues = Object.fromEntries(new FormData(this.element));
        switch (formValues.country) {
            case 'US':
                this.generateStateSelectOptions([...MILITARY_US_STATES, ...US_STATES]);
                this.hideMxVatIdInput();
                this.hideBrVatIdInput();
                break;
            case 'CA':
                this.generateStateSelectOptions(CA_STATES);
                this.hideMxVatIdInput();
                this.hideBrVatIdInput();
                break;
            case 'JP':
                this.generateStateSelectOptions(JP_STATES);
                this.hideMxVatIdInput();
                this.hideBrVatIdInput();
                break;
            case 'AU':
                this.generateStateSelectOptions(AU_STATES);
                this.hideMxVatIdInput();
                this.hideBrVatIdInput();
                break;
            case 'BR':
                this.generateStateSelectOptions(BR_STATES);
                this.showBrVatIdInput();
                this.hideMxVatIdInput();
                break;
            case 'MX':
                this.showMxVatIdInput();
                this.hideBrVatIdInput();
                break;
            default:
                this.showStateInput();
                this.hideStateSelect();
                this.hideBrVatIdInput();
                this.hideMxVatIdInput();
                break;
        }
    }
    async validateShippingAddress() {
        this.submitButtonTarget.dataset.loading = 'true';
        this.suggestion = null;
        const formData = new FormData(this.element);
        const values = Object.fromEntries(formData);
        const response = await fetch(isProduction()
            ? 'https://api.fourthwall.com/api/shipping/address/validate'
            : 'https://api.staging.fourthwall.com/api/shipping/address/validate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                shippingAddress: {
                    firstName: values.name,
                    lastName: values.name,
                    address1: values.address1,
                    address2: values.address2,
                    city: values.city,
                    state: values.state,
                    country: values.country,
                    zip: values.zip,
                },
            }),
        });
        delete this.submitButtonTarget.dataset.loading;
        if (!response.ok)
            return;
        const jsonResponse = await response.json();
        switch (jsonResponse.type) {
            case 'ADDRESS_ERROR':
            case 'ADDRESS_VERIFICATION_ERROR':
                this.openErrorModal(values);
                break;
            case 'ADDRESS_SUGGESTION':
                this.suggestion = jsonResponse.addressSuggestion;
                this.openSuggestionModal(jsonResponse.addressSuggestion);
                break;
            default:
                this.submitForm();
                break;
        }
    }
    openErrorModal(values) {
        const countryName = getCountryNameByCode(values.country.toString());
        const stateName = getStateNameByCode(values.country.toString(), values.state.toString());
        this.errorModalTarget.classList.remove('hidden');
        this.errorModalContentTarget.innerHTML = `
      <p>${values.address1}${values.address2 ? ` ${values.address2}` : ''}</p>
      <p>${values.city}, ${stateName || ''} ${values.zip || ''}</p>
      <p>${countryName}</p>
    `;
    }
    openSuggestionModal(suggestion) {
        const countryName = getCountryNameByCode(suggestion.country);
        const stateName = getStateNameByCode(suggestion.country, suggestion.state);
        this.suggestionModalTarget.classList.remove('hidden');
        this.suggestionModalContentTarget.innerHTML = `
      <p>${suggestion.address1}${suggestion.address2 ? ` ${suggestion.address2}` : ''}</p>
      <p>${suggestion.city}, ${stateName || ''} ${suggestion.zip || ''}</p>
      <p>${countryName}</p>
    `;
    }
    useSuggestedAddress() {
        if (!this.suggestion)
            return;
        const elements = this.element.elements;
        Object.entries(this.suggestion).forEach(([key, value]) => {
            const element = elements.namedItem(key);
            if (value && element && 'value' in element) {
                element.value = value;
            }
        });
    }
    submitForm() {
        Rails.fire(this.element, 'submit');
    }
    closeModal() {
        this.errorModalTarget.classList.add('hidden');
        this.suggestionModalTarget.classList.add('hidden');
    }
    hideStateInput() {
        this.stateInputTarget.classList.add('hidden');
        this.stateInputTarget.querySelector('input').disabled = true;
    }
    showStateInput() {
        this.stateInputTarget.classList.remove('hidden');
        this.stateInputTarget.querySelector('input').disabled = false;
    }
    hideStateSelect() {
        this.stateSelectTarget.classList.add('hidden');
        this.stateSelectTarget.querySelector('select').disabled = true;
    }
    showStateSelect() {
        this.stateSelectTarget.classList.remove('hidden');
        this.stateSelectTarget.querySelector('select').disabled = false;
    }
    showBrVatIdInput() {
        this.brVatIdInputTarget.classList.remove('hidden');
        this.brVatIdInputTarget.querySelector('input').disabled = false;
    }
    hideBrVatIdInput() {
        this.brVatIdInputTarget.classList.add('hidden');
        this.brVatIdInputTarget.querySelector('input').disabled = true;
    }
    showMxVatIdInput() {
        this.mxVatIdInputTarget.classList.remove('hidden');
        this.mxVatIdInputTarget.querySelector('input').disabled = false;
    }
    hideMxVatIdInput() {
        this.mxVatIdInputTarget.classList.add('hidden');
        this.mxVatIdInputTarget.querySelector('input').disabled = true;
    }
    validateForm() {
        const formValidation = z
            .object({
            name: z
                .string()
                .trim()
                .min(1, { error: 'This field is required.' })
                .max(35, { error: 'A recipient full name has more than 35 characters.' })
                .refine((value) => value.trim().includes(' '), {
                error: 'A recipient full name must contain both first and last name.',
            })
                .refine((value) => isStringLatin(value), {
                error: 'A recipient full name contains invalid characters.',
            }),
            address1: z
                .string()
                .trim()
                .min(1, { error: 'This field is required.' })
                .max(35, { error: 'A recipient full name has more than 35 characters.' })
                .refine((value) => !/^[0-9]*$/.test(value), {
                error: 'A shipping street address contains only numbers.',
            })
                .refine((value) => isStringLatin(value), {
                error: 'A shippingstreet address contains invalid characters.',
            }),
            address2: z
                .string()
                .trim()
                .optional()
                .refine((value) => (value ? isStringLatin(value) : true), {
                error: 'A shipping apartment contains invalid characters.',
            }),
            city: z
                .string()
                .trim()
                .min(1, { error: 'This field is required.' })
                .max(27, { error: 'A shipping city has more than 27 characters.' })
                .refine((value) => (value ? isStringLatin(value) : true), {
                error: 'A shipping city contains invalid characters.',
            }),
            country: z.string().trim().min(1, { error: 'This field is required.' }),
            state: z
                .string()
                .max(30, { error: 'A shipping state has more than 30 characters.' })
                .optional()
                .refine((value) => (value ? isStringLatin(value) : true), {
                error: 'A shipping state contains invalid characters.',
            }),
            stateSelect: z
                .string()
                .trim()
                .max(30, { error: 'A shipping state has more than 30 characters.' })
                .optional()
                .refine((value) => (value ? isStringLatin(value) : true), {
                error: 'A shipping state contains invalid characters.',
            }),
            zip: z
                .string()
                .trim()
                .max(20, { error: 'A shipping zip or postal code has more than 20 characters.' })
                .refine((value) => (value ? isStringLatin(value) : true), {
                error: 'A shipping zip or postal code contains invalid characters.',
            }),
            phone: z
                .string()
                .trim()
                .min(1, { error: 'This field is required.' })
                .max(30, { error: 'A recipient phone number has more than 30 characters.' })
                .refine((value) => (value ? isStringLatin(value) : true), {
                error: 'A recipient phone number contains invalid characters.',
            })
                .refine((value) => (value ? isPhoneNumber(value) : true), {
                error: 'A recipient phone number has invalid format.',
            }),
            brVatIdInput: z.string().trim().optional(),
            mxVatIdInput: z.string().trim().optional(),
        })
            .partial()
            .superRefine((data, ctx) => {
            if (data.country &&
                ['US', 'CA', 'JP', 'AU', 'BR'].includes(data.country) &&
                isEmpty(data.state)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'This field is required',
                    path: ['stateSelect'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.country &&
                !COUNTRIES_NOT_REQUIRED_ZIP.includes(data.country) &&
                isEmpty(data.zip)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'This field is required.',
                    path: ['zip'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.zip && data.country === 'US' && !isUSZipCode(data.zip)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A shipping zip or postal code is not in valid format.',
                    path: ['zip'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.zip && data.country === 'CA' && !isCAZipCode(data.zip)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A shipping zip or postal code is not in valid format.',
                    path: ['zip'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.zip && data.country === 'AU' && !isAUZipCode(data.zip)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A shipping zip or postal code is not in valid format.',
                    path: ['zip'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.country && data.country === 'BR' && isEmpty(data.brVatIdInput)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'This field is required.',
                    path: ['brVatIdInput'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.country &&
                data.country === 'BR' &&
                data.brVatIdInput &&
                !isCPFNumber(data.brVatIdInput)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A recipient CPF number has invalid format (***.***.***-**).',
                    path: ['brVatIdInput'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.country && data.country === 'MX' && isEmpty(data.mxVatIdInput)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'This field is required.',
                    path: ['mxVatIdInput'],
                });
            }
        })
            .superRefine((data, ctx) => {
            if (data.country &&
                data.country === 'MX' &&
                data.mxVatIdInput &&
                !(isCURPNumber(data.mxVatIdInput) || isRFCNumber(data.mxVatIdInput))) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A recipient tax ID must be a valid CURP or RFC number.',
                    path: ['mxVatIdInput'],
                });
            }
        });
        const formValues = Object.fromEntries(new FormData(this.element));
        if (formValues.wants_to_receive_benefits === 'false') {
            return true;
        }
        const values = {
            name: formValues.name,
            address1: formValues.address1,
            address2: formValues.address2,
            city: formValues.city,
            country: formValues.country,
            state: formValues.state,
            stateSelect: formValues.state,
            zip: formValues.zip,
            phone: formValues.phone,
            brVatIdInput: formValues.vat_id,
            mxVatIdInput: formValues.vat_id,
        };
        const result = formValidation.safeParse(values);
        if (result.success) {
            Object.keys(values).forEach((field) => {
                const inputErrorElement = document.querySelector(`#${field}`)?.parentElement
                    ?.nextElementSibling;
                inputErrorElement.innerHTML = '';
            });
            return true;
        }
        if (!result.success) {
            const fieldErrors = z.treeifyError(result.error);
            const errors = {};
            Object.keys(fieldErrors.properties || {}).forEach((field) => {
                errors[field] =
                    fieldErrors.properties?.[field]?.errors[0];
            });
            Object.keys(values).forEach((field) => {
                const inputErrorElement = document.querySelector(`#${field}`)?.parentElement
                    ?.nextElementSibling;
                inputErrorElement.innerHTML = errors[field] || '';
            });
            return false;
        }
    }
    handleSubmit() {
        const formValues = Object.fromEntries(new FormData(this.element));
        if (formValues.wants_to_receive_benefits === 'false') {
            return this.submitForm();
        }
        this.submitCount = this.submitCount ? this.submitCount + 1 : 1;
        const isValid = this.validateForm();
        if (isValid)
            this.validateShippingAddress();
    }
    handleChange() {
        if (this.submitCount && this.submitCount > 0) {
            this.validateForm();
        }
    }
    documentMorph() {
        this.connect();
    }
}
